import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { History } from 'history';
import { Publisher, showErrorToastNotification } from '../../../@data';
import {
  getDimensions as getMediationDimensions,
  getGranularity as getMediationGranularity,
  getMetrics as getMediationMetrics,
  getMediationReportData,
} from '../../../@data/services/MediationReportingService';
import {
  getDataDelay,
  getDimensions,
  getGranularity,
  getMetrics,
  getPubCenterMetadataDimensions,
  getPubCenterMetaDataItem,
  getPubCenterMetaDataReportTypes,
  getPubCenterReportData,
  getReportData,
} from '../../../@data/services/ReportingService';
import { MediationReportingDataRequestBody } from '../../../@data/store/schema/models/MediationReportingDataRequestBody';
import { PubCenterReportingDataRequestBody, ReportingDataRequestBody } from '../../../@data/store/schema/models/ReportingDataRequestBody';
import { getAdunitStore } from '../../ad-management/Adunit/@data';
import { fetchPublisherProfileDetails } from '../../ad-management/Profile/@data/services';
import { changeReport, navigateToReportByName, onChangeReportId, onChangeSavedReports } from '../@data/actions';
import { TythonColumnsEnum } from '../@data/store/schema/ColumnsEnum';
import { getPublisherId } from '../@data/store/store';
import { ISavedReportItem } from '../components/LeftHandNavigation/LeftHandNavigation.types';
import {
  addSavedReport,
  deleteSavedReport,
  getSavedReports,
  updateSavedReport,
} from '../components/LeftHandNavigation/services/reportNavigationService';
import { IReportsMetadata, MediationReportingDataRow, PubCenterReportingDataRow } from '../DashboardCards/@data/store/schema';
import { getReportType } from '../DashboardCards/@data/store/selectors';
import { mapAdUnitName } from '../DashboardCards/@data/utils/mapAdUnitName';
import { concatObjectsByField } from '../DashboardCards/ReportCard/ReportCard.helpers';
import { ChartGrain, ReportCardType } from '../DashboardCards/ReportCard/ReportCard.types';
import ReportMessages from '../Report.messages';

export function useReportQuery(reportRequestBody: ReportingDataRequestBody, isDetailedReport: boolean, enabled: boolean) {
  const { isLoading, isError, data, error } = useQuery({
    queryKey: ['reportData', { ...reportRequestBody, columns: reportRequestBody.columns.sort(), callerGuid: '' }],
    queryFn: async () => {
      const { data: reportData } = await getReportData(reportRequestBody, new AbortController());
      return reportData.Data;
    },
    onError: () => {
      const isSummaryReport = ReportCardType[reportRequestBody.reportName] === ReportCardType.SUMMARY;
      const messageDescriptor = isSummaryReport
        ? ReportMessages.loadingReportSummaryFailedMessage
        : isDetailedReport
        ? ReportMessages.loadingDetailedReportFailedMessage
        : ReportMessages.loadingReportFailedMessage;
      showErrorToastNotification({
        textMessageDescriptor: messageDescriptor,
        intlMessageValues: { reportType: reportRequestBody.reportName.toLowerCase() },
      });
    },
    enabled,
    staleTime: 3600000,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });
  return { isLoading, isError, data, error };
}
export function useMediationReportQuery(reportRequestBody: MediationReportingDataRequestBody, enabled: boolean) {
  const { isLoading, isError, data, error } = useQuery({
    queryKey: ['reportData', { ...reportRequestBody, columns: reportRequestBody.columns.sort(), callerGuid: '' }],
    queryFn: async () => {
      const { data: reportData } = await getMediationReportData(reportRequestBody, new AbortController());
      const {
        adUnitsRaw: { data: adUnitsData },
      } = getAdunitStore();
      const transformedData: MediationReportingDataRow[] = mapAdUnitName(reportData, adUnitsData);
      return transformedData;
    },
    onError: () => {
      showErrorToastNotification({
        textMessageDescriptor: ReportMessages.loadingMediationReportFailedMessage,
      });
    },
    enabled: enabled && !getAdunitStore().isFetchingAdUnitStatus,
    staleTime: 3600000,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });
  return { isLoading, isError, data, error };
}

export function useReportMetadata() {
  const { isLoading, isError, data, error } = useQuery({
    queryKey: ['reportMetadata'],
    queryFn: async () => {
      const reportType = getReportType();
      const [dimensions, metrics, grains] = await Promise.all([
        getDimensions(reportType),
        getMetrics(reportType),
        getGranularity(reportType),
      ]);
      return { dimensions, metrics, grains } as IReportsMetadata;
    },
    onError: () => {
      showErrorToastNotification({ textMessageDescriptor: ReportMessages.loadingReportMetadataFailedMessage });
    },
    staleTime: 24 * 60 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });
  return { isLoading, isError, data, error };
}
export function useMediationReportMetadata(enabled = true) {
  const { isLoading, isError, data, error } = useQuery({
    queryKey: ['mediationReportMetadata'],
    queryFn: async () => {
      const reportType = getReportType();
      const [dimensions, metrics, grains] = await Promise.all([
        getMediationDimensions(reportType),
        getMediationMetrics(reportType),
        getMediationGranularity(reportType),
      ]);
      return { dimensions, metrics, grains } as IReportsMetadata;
    },
    onError: () => {
      showErrorToastNotification({ textMessageDescriptor: ReportMessages.loadingReportMetadataFailedMessage });
    },
    enabled,
    staleTime: 24 * 60 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });
  return { isLoading, isError, data, error };
}

export function usePubCenterReportQuery(reportRequestBody: PubCenterReportingDataRequestBody, enabled: boolean) {
  const { isLoading, isError, data, error } = useQuery({
    queryKey: ['pubCenterReportData', { ...reportRequestBody, columns: reportRequestBody.columns }],
    queryFn: async () => {
      const { data: reportData } = await getPubCenterReportData(reportRequestBody, new AbortController());
      return {
        ...reportData,
        Data: reportData.Data.map((row: PubCenterReportingDataRow) => {
          if (row.Date !== undefined && row.Date.split('/').length === 3) {
            const [month, day, year] = row.Date.split('/').map(Number);
            return { ...row, Date: new Date(Date.UTC(year, month - 1, day, row.Hour ?? 0)).toISOString() };
          } else if (row.Date !== undefined) {
            const dateObj = new Date(row.Date);

            return {
              ...row,
              Date: new Date(Date.UTC(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate(), row.Hour ?? 0)).toISOString(),
            };
          }
          return row;
        }),
      };
    },
    onError: () => {
      if (reportRequestBody.granularity.toLowerCase() !== ChartGrain.NONE.toLowerCase()) {
        showErrorToastNotification({
          textMessageDescriptor: ReportMessages.loadingReportFailedMessage,
          intlMessageValues: { reportType: reportRequestBody.reportName.toLowerCase() },
        });
      }
    },
    retry: (failureCount: number, errorRes: Error) => {
      if (failureCount >= 3) {
        return false;
      }
      try {
        const errorMessage = JSON.parse(errorRes.message as string)[0];
        return !errorMessage?.Description?.includes('The number of rows in report exceeds');
      } catch (err) {
        return false;
      }
    },
    enabled: enabled,
    staleTime: 3600000,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });
  return { isLoading, isError, data, error };
}

export function usePubCenterReportTopKQuery(
  requestBody: PubCenterReportingDataRequestBody,
  mainDimension: TythonColumnsEnum,
  extraDimensions: TythonColumnsEnum[] = []
) {
  const { isLoading, isError, data, error } = useQuery({
    queryKey: ['pubCenterReportTopKData', { ...requestBody, columns: requestBody.columns }],
    queryFn: async () => {
      try {
        const metricData = await Promise.all(
          requestBody.columns.map((column) =>
            column !== mainDimension
              ? getPubCenterReportData(
                  {
                    ...requestBody,
                    columns: [mainDimension, ...extraDimensions, column],
                    requestOptions: { ...requestBody.requestOptions, OrderBy: column },
                  },
                  new AbortController()
                )
              : null
          )
        );
        const flattenData = Object.values(metricData.flatMap((item) => item?.data.Data)).filter(
          (row): row is PubCenterReportingDataRow =>
            row !== undefined && !(row[mainDimension].startsWith('Other_') && row[mainDimension].endsWith('_DisplayName'))
        );
        return concatObjectsByField<PubCenterReportingDataRow>(flattenData, mainDimension, requestBody.columns);
      } catch (error) {
        return (
          await getPubCenterReportData(
            { ...requestBody, columns: [...requestBody.columns, mainDimension, ...extraDimensions], requestOptions: { isUIClient: true } },
            new AbortController()
          )
        ).data.Data;
      }
    },
    onError: async () => {
      showErrorToastNotification({
        textMessageDescriptor: ReportMessages.loadingReportFailedMessage,
        intlMessageValues: { reportType: requestBody.reportName.toLowerCase() },
      });
    },
    staleTime: 3600000,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });
  return { isLoading, isError, data, error };
}

export function usePubCenterReportMetadata(reportType: string, isOverviewPage: boolean) {
  const { isLoading, isError, data, error } = useQuery({
    queryKey: ['pubCenterReportMetadata', reportType],
    queryFn: async () => {
      const [metrics, dimensions, grains, filters] = await Promise.all([
        getPubCenterMetaDataItem(reportType, 'Metrics'),
        getPubCenterMetaDataItem(reportType, 'Dimensions'),
        getPubCenterMetaDataItem(reportType, 'Grains'),
        isOverviewPage ? undefined : getPubCenterMetaDataItem(reportType, 'Filters'),
      ]);
      return { dimensions, metrics, grains, filters } as IReportsMetadata;
    },
    onError: () => {
      showErrorToastNotification({ textMessageDescriptor: ReportMessages.loadingReportMetadataFailedMessage });
    },
    staleTime: 24 * 60 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
    enabled: true,
  });
  return { isLoading, isError, data, error };
}

export function usePubCenterReportTypes() {
  const { isLoading, isError, data, error } = useQuery({
    queryKey: ['pubCenterReportTypes', getPublisherId()],
    queryFn: async () => {
      return await getPubCenterMetaDataReportTypes('reportType');
    },
    onError: () => {
      showErrorToastNotification({ textMessageDescriptor: ReportMessages.loadingReportTypesFailedMessage });
    },
    staleTime: 24 * 60 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });
  return { isLoading, isError, data, error };
}

export function useSavedReports() {
  const { isLoading, isError, data, error } = useQuery({
    queryKey: ['pubCenterSavedReports', getPublisherId()],
    queryFn: async () => {
      const savedReports = await getSavedReports();
      onChangeSavedReports(savedReports);
      return savedReports;
    },
    onError: () => {
      showErrorToastNotification({ textMessageDescriptor: ReportMessages.loadingReportTypesFailedMessage });
    },
    staleTime: 24 * 60 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });
  return { isLoading, isError, data, error };
}

export function usePostReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ currentReport, history }: { currentReport: ISavedReportItem; history: History<unknown> }) =>
      addSavedReport(currentReport),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['pubCenterSavedReports', getPublisherId()] });
      navigateToReportByName(variables.currentReport.name, variables.history);
    },
  });
}

export function useEditReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (savedReport: ISavedReportItem) => {
      const updatedReport = await updateSavedReport(savedReport);
      updatedReport && changeReport(updatedReport);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pubCenterSavedReports', getPublisherId()] });
    },
  });
}

export function useDeleteReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ history, reportId }: { history: History<unknown>; reportId: string }) => deleteSavedReport(reportId),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['pubCenterSavedReports', getPublisherId()] });
      onChangeReportId(undefined, variables.history);
    },
  });
}

export function usePubCenterReportMetadaDimensions(publisherId, dimensionType, hierarchy?: string) {
  const { isLoading, isError, data, error } = useQuery({
    queryKey: ['pubCenterReportMetadataDimensions', publisherId, dimensionType, hierarchy],
    enabled:
      !!dimensionType &&
      dimensionType !== 'PartnerTag' &&
      dimensionType !== 'FormCode' &&
      dimensionType !== 'TypeTag' &&
      dimensionType !== 'AdType' &&
      dimensionType !== 'MarketCountry' &&
      dimensionType !== 'MarketLanguage' &&
      dimensionType !== 'ContentExperience' &&
      dimensionType !== 'Experience' &&
      dimensionType !== 'ProjectId',
    queryFn: async () => {
      if (hierarchy) {
        return await getPubCenterMetadataDimensions(publisherId, dimensionType, hierarchy);
      }
      return await getPubCenterMetadataDimensions(publisherId, dimensionType);
    },
    onError: () => {
      showErrorToastNotification({ textMessageDescriptor: ReportMessages.loadingReportMetadataFailedMessage });
    },
    staleTime: 24 * 60 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });
  return { isLoading, isError, data, error };
}

export function usePublisherProfileDetails() {
  const { isLoading, isError, data, error } = useQuery({
    queryKey: ['publisherProfileDetails', getPublisherId()],
    queryFn: async () => {
      const publisherId = getPublisherId();
      if (!publisherId) {
        throw new Error('Publisher ID is not defined');
      }
      const publisherData = await fetchPublisherProfileDetails(new Publisher(getPublisherId()));
      return publisherData;
    },
    onError: () => {
      showErrorToastNotification({ textMessageDescriptor: ReportMessages.loadingPublisherProfileFailedMessage });
    },
    staleTime: 24 * 60 * 60 * 1000,
  });
  return { isLoading, isError, data, error };
}

export function useDataDelay(reportTypes: string[], startDate: string, endDate: string) {
  const { isLoading, isError, data, error } = useQuery({
    queryKey: ['dataDelay', getPublisherId(), reportTypes, startDate, endDate],
    queryFn: async () => {
      return await getDataDelay({
        ReportTypes: reportTypes,
        RequestedDateRange: {
          StartDateTime: startDate,
          EndDateTime: endDate,
        },
      });
    },
    enabled: reportTypes.length > 0 && !!startDate && !!endDate,
  });

  return { isLoading, isError, data, error };
}
